import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { usePublicLanguage } from '../../contexts/PublicLanguageContext';
import { 
  Shield, 
  Lock, 
  Server, 
  Eye, 
  Trash2, 
  Brain, 
  CheckCircle,
  AlertTriangle,
  Database,
  UserCheck,
  Settings,
  FileText,
  Download,
  Clock,
  MapPin
} from 'lucide-react';

const SecurityPrivacyPage = () => {
  const { t } = usePublicLanguage();
  const [activeTab, setActiveTab] = useState('data-hosting');
  
  // Helper function to safely get array translations
  const getArrayTranslation = (key) => {
    const value = t(key);
    return Array.isArray(value) ? value : [];
  };

  const tabs = [
    {
      id: 'data-hosting',
      label: t('security.tabs.dataHosting.label'),
      icon: Server,
      description: t('security.tabs.dataHosting.description')
    },
    {
      id: 'gdpr',
      label: t('security.tabs.gdpr.label'),
      icon: Shield,
      description: t('security.tabs.gdpr.description')
    },
    {
      id: 'data-usage',
      label: t('security.tabs.dataUsage.label'),
      icon: Eye,
      description: t('security.tabs.dataUsage.description')
    },
    {
      id: 'user-control',
      label: t('security.tabs.userControl.label'),
      icon: UserCheck,
      description: t('security.tabs.userControl.description')
    },
    {
      id: 'ai-security',
      label: t('security.tabs.aiSecurity.label'),
      icon: Brain,
      description: t('security.tabs.aiSecurity.description')
    }
  ];

  const securityFeatures = [
    {
      icon: Lock,
      title: t('security.features.encryption.title'),
      description: t('security.features.encryption.description')
    },
    {
      icon: Shield,
      title: t('security.features.gdprCompliance.title'),
      description: t('security.features.gdprCompliance.description')
    },
    {
      icon: Database,
      title: t('security.features.secureHosting.title'),
      description: t('security.features.secureHosting.description')
    },
    {
      icon: Trash2,
      title: t('security.features.rightToForget.title'),
      description: t('security.features.rightToForget.description')
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'data-hosting':
        return (
          <div className="space-y-8">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-start space-x-3">
                <MapPin className="h-6 w-6 text-blue-600 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">
                    {t('security.dataHosting.serverLocation.title')}
                  </h3>
                  <p className="text-blue-800" dangerouslySetInnerHTML={{
                    __html: t('security.dataHosting.serverLocation.description')
                  }}></p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <Server className="h-8 w-8 text-primary-600 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('security.dataHosting.infrastructure.title')}</h3>
                <ul className="space-y-2 text-gray-600">
                  {getArrayTranslation('security.dataHosting.infrastructure.items').map((item, index) => (
                    <li key={index} className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <Lock className="h-8 w-8 text-primary-600 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('security.dataHosting.encryption.title')}</h3>
                <ul className="space-y-2 text-gray-600">
                  {getArrayTranslation('security.dataHosting.encryption.items').map((item, index) => (
                    <li key={index} className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        );

      case 'gdpr':
        return (
          <div className="space-y-8">
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <div className="flex items-start space-x-3">
                <Shield className="h-6 w-6 text-green-600 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-green-900 mb-2">
                    {t('security.gdpr.compliance.title')}
                  </h3>
                  <p className="text-green-800">
                    {t('security.gdpr.compliance.description')}
                  </p>
                </div>
              </div>
            </div>

            <div className="grid gap-6">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <FileText className="h-5 w-5 text-primary-600 mr-2" />
                  {t('security.gdpr.rights.title')}
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="flex items-start space-x-2">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                      <div>
                        <div className="font-medium text-gray-900">{t('security.gdpr.rights.access.title')}</div>
                        <div className="text-sm text-gray-600">{t('security.gdpr.rights.access.description')}</div>
                      </div>
                    </div>
                    <div className="flex items-start space-x-2">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                      <div>
                        <div className="font-medium text-gray-900">{t('security.gdpr.rights.rectification.title')}</div>
                        <div className="text-sm text-gray-600">{t('security.gdpr.rights.rectification.description')}</div>
                      </div>
                    </div>
                    <div className="flex items-start space-x-2">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                      <div>
                        <div className="font-medium text-gray-900">{t('security.gdpr.rights.erasure.title')}</div>
                        <div className="text-sm text-gray-600">{t('security.gdpr.rights.erasure.description')}</div>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start space-x-2">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                      <div>
                        <div className="font-medium text-gray-900">{t('security.gdpr.rights.portability.title')}</div>
                        <div className="text-sm text-gray-600">{t('security.gdpr.rights.portability.description')}</div>
                      </div>
                    </div>
                    <div className="flex items-start space-x-2">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                      <div>
                        <div className="font-medium text-gray-900">{t('security.gdpr.rights.objection.title')}</div>
                        <div className="text-sm text-gray-600">{t('security.gdpr.rights.objection.description')}</div>
                      </div>
                    </div>
                    <div className="flex items-start space-x-2">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                      <div>
                        <div className="font-medium text-gray-900">{t('security.gdpr.rights.restriction.title')}</div>
                        <div className="text-sm text-gray-600">{t('security.gdpr.rights.restriction.description')}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Clock className="h-5 w-5 text-primary-600 mr-2" />
                  {t('security.gdpr.processingTimes.title')}
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">{t('security.gdpr.processingTimes.hours24')}</div>
                    <div className="text-sm text-blue-800">{t('security.gdpr.processingTimes.acknowledgment')}</div>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">{t('security.gdpr.processingTimes.days7')}</div>
                    <div className="text-sm text-green-800">{t('security.gdpr.processingTimes.standard')}</div>
                  </div>
                  <div className="text-center p-4 bg-orange-50 rounded-lg">
                    <div className="text-2xl font-bold text-orange-600">{t('security.gdpr.processingTimes.days30')}</div>
                    <div className="text-sm text-orange-800">{t('security.gdpr.processingTimes.maximum')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'data-usage':
        return (
          <div className="space-y-8">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
              <div className="flex items-start space-x-3">
                <Eye className="h-6 w-6 text-purple-600 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-purple-900 mb-2">
                    {t('security.dataUsage.exclusive.title')}
                  </h3>
                  <p className="text-purple-800" dangerouslySetInnerHTML={{
                    __html: t('security.dataUsage.exclusive.description')
                  }}></p>
                </div>
              </div>
            </div>

            <div className="grid gap-6">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  {t('security.dataUsage.whatWeDo.title')}
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3 p-4 bg-green-50 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                    <div>
                      <div className="font-medium text-green-900">{t('security.dataUsage.whatWeDo.analysis.title')}</div>
                      <div className="text-sm text-green-700">{t('security.dataUsage.whatWeDo.analysis.description')}</div>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 p-4 bg-green-50 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                    <div>
                      <div className="font-medium text-green-900">{t('security.dataUsage.whatWeDo.management.title')}</div>
                      <div className="text-sm text-green-700">{t('security.dataUsage.whatWeDo.management.description')}</div>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 p-4 bg-green-50 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                    <div>
                      <div className="font-medium text-green-900">{t('security.dataUsage.whatWeDo.improvement.title')}</div>
                      <div className="text-sm text-green-700">{t('security.dataUsage.whatWeDo.improvement.description')}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
                  {t('security.dataUsage.whatWeDont.title')}
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3 p-4 bg-red-50 rounded-lg">
                    <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5" />
                    <div>
                      <div className="font-medium text-red-900">{t('security.dataUsage.whatWeDont.dataSale.title')}</div>
                      <div className="text-sm text-red-700">{t('security.dataUsage.whatWeDont.dataSale.description')}</div>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 p-4 bg-red-50 rounded-lg">
                    <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5" />
                    <div>
                      <div className="font-medium text-red-900">{t('security.dataUsage.whatWeDont.commercialSharing.title')}</div>
                      <div className="text-sm text-red-700">{t('security.dataUsage.whatWeDont.commercialSharing.description')}</div>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 p-4 bg-red-50 rounded-lg">
                    <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5" />
                    <div>
                      <div className="font-medium text-red-900">{t('security.dataUsage.whatWeDont.advertising.title')}</div>
                      <div className="text-sm text-red-700">{t('security.dataUsage.whatWeDont.advertising.description')}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'user-control':
        return (
          <div className="space-y-8">
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-6">
              <div className="flex items-start space-x-3">
                <UserCheck className="h-6 w-6 text-indigo-600 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-indigo-900 mb-2">
                    {t('security.userControl.fullControl.title')}
                  </h3>
                  <p className="text-indigo-800">
                    {t('security.userControl.fullControl.description')}
                  </p>
                </div>
              </div>
            </div>

            <div className="grid gap-6">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Settings className="h-5 w-5 text-primary-600 mr-2" />
                  {t('security.userControl.features.title')}
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3 p-4 border border-gray-200 rounded-lg">
                      <Download className="h-5 w-5 text-blue-500 mt-0.5" />
                      <div>
                        <div className="font-medium text-gray-900">{t('security.userControl.features.export.title')}</div>
                        <div className="text-sm text-gray-600">{t('security.userControl.features.export.description')}</div>
                        <div className="text-xs text-blue-600 mt-1">{t('security.userControl.features.export.location')}</div>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3 p-4 border border-gray-200 rounded-lg">
                      <Trash2 className="h-5 w-5 text-red-500 mt-0.5" />
                      <div>
                        <div className="font-medium text-gray-900">{t('security.userControl.features.delete.title')}</div>
                        <div className="text-sm text-gray-600">{t('security.userControl.features.delete.description')}</div>
                        <div className="text-xs text-red-600 mt-1">{t('security.userControl.features.delete.location')}</div>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3 p-4 border border-gray-200 rounded-lg">
                      <Eye className="h-5 w-5 text-green-500 mt-0.5" />
                      <div>
                        <div className="font-medium text-gray-900">{t('security.userControl.features.view.title')}</div>
                        <div className="text-sm text-gray-600">{t('security.userControl.features.view.description')}</div>
                        <div className="text-xs text-green-600 mt-1">{t('security.userControl.features.view.location')}</div>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3 p-4 border border-gray-200 rounded-lg">
                      <Settings className="h-5 w-5 text-purple-500 mt-0.5" />
                      <div>
                        <div className="font-medium text-gray-900">{t('security.userControl.features.preferences.title')}</div>
                        <div className="text-sm text-gray-600">{t('security.userControl.features.preferences.description')}</div>
                        <div className="text-xs text-purple-600 mt-1">{t('security.userControl.features.preferences.location')}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {t('security.userControl.deletionProcess.title')}
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                    <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-bold">1</div>
                    <div>
                      <div className="font-medium text-gray-900">{t('security.userControl.deletionProcess.step1.title')}</div>
                      <div className="text-sm text-gray-600">{t('security.userControl.deletionProcess.step1.description')}</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                    <div className="w-8 h-8 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center text-sm font-bold">2</div>
                    <div>
                      <div className="font-medium text-gray-900">{t('security.userControl.deletionProcess.step2.title')}</div>
                      <div className="text-sm text-gray-600">{t('security.userControl.deletionProcess.step2.description')}</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                    <div className="w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-sm font-bold">3</div>
                    <div>
                      <div className="font-medium text-gray-900">{t('security.userControl.deletionProcess.step3.title')}</div>
                      <div className="text-sm text-gray-600">{t('security.userControl.deletionProcess.step3.description')}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'ai-security':
        return (
          <div className="space-y-8">
            <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-6">
              <div className="flex items-start space-x-3">
                <Brain className="h-6 w-6 text-cyan-600 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-cyan-900 mb-2">
                    {t('security.aiSecurity.protection.title')}
                  </h3>
                  <p className="text-cyan-800" dangerouslySetInnerHTML={{
                    __html: t('security.aiSecurity.protection.description')
                  }}></p>
                </div>
              </div>
            </div>

            <div className="grid gap-6">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Brain className="h-5 w-5 text-primary-600 mr-2" />
                  {t('security.aiSecurity.models.title')}
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 border border-blue-200 bg-blue-50 rounded-lg">
                    <div className="font-medium text-blue-900 mb-2">{t('security.aiSecurity.models.openai.title')}</div>
                    <div className="text-sm text-blue-700 mb-3">
                      {t('security.aiSecurity.models.openai.description')}
                    </div>
                    <div className="text-xs text-blue-600 font-medium">✓ {t('security.aiSecurity.models.openai.certification')}</div>
                  </div>
                  <div className="p-4 border border-green-200 bg-green-50 rounded-lg">
                    <div className="font-medium text-green-900 mb-2">{t('security.aiSecurity.models.local.title')}</div>
                    <div className="text-sm text-green-700 mb-3">
                      {t('security.aiSecurity.models.local.description')}
                    </div>
                    <div className="text-xs text-green-600 font-medium">✓ {t('security.aiSecurity.models.local.guarantee')}</div>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Lock className="h-5 w-5 text-primary-600 mr-2" />
                  {t('security.aiSecurity.protectionMeasures.title')}
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3 p-4 bg-green-50 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                    <div>
                      <div className="font-medium text-green-900">{t('security.aiSecurity.protectionMeasures.anonymization.title')}</div>
                      <div className="text-sm text-green-700">
                        {t('security.aiSecurity.protectionMeasures.anonymization.description')}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 p-4 bg-green-50 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                    <div>
                      <div className="font-medium text-green-900">{t('security.aiSecurity.protectionMeasures.encryption.title')}</div>
                      <div className="text-sm text-green-700">
                        {t('security.aiSecurity.protectionMeasures.encryption.description')}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 p-4 bg-green-50 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                    <div>
                      <div className="font-medium text-green-900">{t('security.aiSecurity.protectionMeasures.noStorage.title')}</div>
                      <div className="text-sm text-green-700">
                        {t('security.aiSecurity.protectionMeasures.noStorage.description')}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 p-4 bg-green-50 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                    <div>
                      <div className="font-medium text-green-900">{t('security.aiSecurity.protectionMeasures.realtime.title')}</div>
                      <div className="text-sm text-green-700">
                        {t('security.aiSecurity.protectionMeasures.realtime.description')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {t('security.aiSecurity.purposes.title')}
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="text-center p-4 border border-gray-200 rounded-lg">
                    <Brain className="h-8 w-8 text-blue-500 mx-auto mb-3" />
                    <div className="font-medium text-gray-900 mb-2">{t('security.aiSecurity.purposes.semantic.title')}</div>
                    <div className="text-sm text-gray-600">
                      {t('security.aiSecurity.purposes.semantic.description')}
                    </div>
                  </div>
                  <div className="text-center p-4 border border-gray-200 rounded-lg">
                    <Settings className="h-8 w-8 text-green-500 mx-auto mb-3" />
                    <div className="font-medium text-gray-900 mb-2">{t('security.aiSecurity.purposes.classification.title')}</div>
                    <div className="text-sm text-gray-600">
                      {t('security.aiSecurity.purposes.classification.description')}
                    </div>
                  </div>
                  <div className="text-center p-4 border border-gray-200 rounded-lg">
                    <FileText className="h-8 w-8 text-purple-500 mx-auto mb-3" />
                    <div className="font-medium text-gray-900 mb-2">{t('security.aiSecurity.purposes.synthesis.title')}</div>
                    <div className="text-sm text-gray-600">
                      {t('security.aiSecurity.purposes.synthesis.description')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Header Section */}
      <section className="bg-gradient-to-br from-primary-600 to-primary-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="inline-flex items-center px-4 py-2 bg-white/20 text-white rounded-full text-sm font-medium mb-6">
              <Shield className="h-4 w-4 mr-2" />
              {t('security.hero.badge')}
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">
              {t('security.hero.title')}
            </h1>
            <p className="text-xl text-primary-100 max-w-3xl mx-auto">
              {t('security.hero.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Security Features Overview */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {t('security.overview.title')}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {t('security.overview.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {securityFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="h-8 w-8 text-primary-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Detailed Information Tabs */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {t('security.detailedInfo.title')}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {t('security.detailedInfo.subtitle')}
            </p>
          </motion.div>

          {/* Tab Navigation */}
          <div className="mb-8">
            <div className="flex flex-wrap justify-center gap-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 px-4 py-3 rounded-lg font-medium transition-all ${
                      activeTab === tab.id
                        ? 'bg-primary-600 text-white shadow-lg'
                        : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="hidden sm:inline">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tab Content */}
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {renderTabContent()}
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="bg-gradient-to-br from-primary-50 to-blue-50 rounded-2xl p-8">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <Shield className="h-8 w-8 text-primary-600" />
              </div>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {t('security.contact.title')}
              </h3>
              <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                {t('security.contact.subtitle')}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/contact"
                  className="bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors"
                >
                  {t('security.contact.teamButton')}
                </a>
                <a
                  href="mailto:security@lediteur.club"
                  className="bg-white text-primary-600 px-6 py-3 rounded-lg font-semibold border border-primary-600 hover:bg-primary-50 transition-colors"
                >
                  {t('security.contact.emailButton')}
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default SecurityPrivacyPage;